import React, { useCallback, useEffect, useRef, useState } from "react";
import MasspayFeeTable, { SmallCashoutLine } from "./MasspayFeeTable";
import { paymentsApi } from "../services/payments";
import { stripeApi } from "../services/stripe";
import type { RailQuote, CashoutOptionsResponse } from "../types/payments";
import { formatSingleAmount } from "../utils/currency";
import { openExternal } from "../lib/openExternal";
import AddDebitCardSheet, { canAddCardInApp } from "./AddDebitCardSheet";
import "./CashoutRailPicker.css";
import { t, MessageKey } from "../i18n";
import { instantExplainerFor, instantUnavailableReason, isKnownInstantLine, InstantUnavailableReason } from "../utils/instantUnavailable";
import InstantUnavailablePopup from "./InstantUnavailablePopup";
import InstantPayoutsPopup from "./InstantPayoutsPopup";
export type { RailQuote } from "../types/payments";

// PROJECT ATLAS — the payout-destination picker inside the cashout modal.
//
// Renders every rail the server says this talent can use, side by side, with
// the REAL net amount for each (never a percentage — "$78.20 arrives" beats
// "3.3% + FX"). When MassPay rails go live (MASSPAY_ENABLED, Phase 1) the same
// component becomes a genuine chooser with zero further UI work.
//
// Instant Payouts: the server adds a `stripe_instant` rail next
// to the free bank rail ONLY for talent who clear the rules (founder: only
// show it to the eligible). Three states, all decided server-side:
//   • needsCard — every rule passed except having a debit card on the Stripe
//     account: "Add card" opens the in-app card sheet (AddDebitCardSheet —
//     Stripe's field, tokenised on the device); on success the picker
//     re-quotes and selects Instant. Without Stripe.js (no key / insecure
//     context) it falls back to their Stripe dashboard, and the picker
//     re-quotes when the app comes back to the foreground.
//   • available — "Instant · ~30 min ✓ / Visa ••4242 · $19.83 · $0.30 fee".
//     The "~30 min" sits IN the title next to "Instant" (founder: the small
//     line under it gets overlooked in a dark booth).
//     The fee is the DJ's; the net shown is what the card receives.
//   • unavailable — an honest one-liner ("No card funds yet"). The row stays
//     grey but answers a tap: a small popup says why, in plain words, and
//     what to do instead (InstantUnavailablePopup, utils/instantUnavailable).
// Every string here is for a DJ in a dark booth: short, plain, one line.
//
// Quotes with isEstimate render a "≈" and an "estimate" tag — MassPay's
// "interchange" line item is undefined until they send the schedule, and
// an instant quote on part of the balance is a proportion until the cashout
// itself settles the cent (compliance model rule 3: never invented precision).

export const RAIL_STRIPE_INSTANT = "stripe_instant";

// Line-drawn icons, the size of the old emoji (no emoji in product UI).
const svgProps = {
  className: "railpicker-svg",
  viewBox: "0 0 24 24",
  width: 20,
  height: 20,
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};
const BankIcon = () => (
  <svg {...svgProps}>
    <path d="M3 9.5 12 4l9 5.5" />
    <path d="M5 10v8M9.5 10v8M14.5 10v8M19 10v8" />
    <path d="M3 20.5h18" />
  </svg>
);
// The instant bolt, in the gold of the instant chip.
const BoltIcon = () => (
  <svg {...svgProps} className="railpicker-svg railpicker-svg--bolt" fill="currentColor" stroke="none">
    <path d="M13.5 2 4.5 13.5H11L10 22l9-11.5h-6.5L13.5 2Z" />
  </svg>
);
const CoinIcon = () => (
  <svg {...svgProps}>
    <circle cx="12" cy="12" r="9" />
    <path d="M14.8 9.2c-.5-.8-1.5-1.2-2.8-1.2-1.7 0-2.8.8-2.8 2s1.1 1.6 2.8 2 2.8.8 2.8 2-1.1 2-2.8 2c-1.3 0-2.3-.4-2.8-1.2M12 6.5V8M12 16v1.5" />
  </svg>
);
// The MassPay wallet.
const WalletIcon = () => (
  <svg {...svgProps}>
    <path d="M19 7V5a2 2 0 0 0-2-2H5a2 2 0 0 0 0 4h14a2 2 0 0 1 2 2v3" />
    <path d="M3 5v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-3" />
    <path d="M17 12h4v4h-4a2 2 0 0 1 0-4z" />
  </svg>
);
const RAIL_ICONS: Record<string, () => React.ReactElement> = {
  stripe_bank: BankIcon,
  stripe_instant: BoltIcon,
  masspay_bank: BankIcon,
  masspay_crypto: CoinIcon,
  masspay_wallet: WalletIcon,
};
const railIcon = (railId: string) => {
  const Icon = RAIL_ICONS[railId] || BankIcon;
  return <Icon />;
};

// The server's grey-row line, in the viewer's language (English unchanged).
const ROW_LINE: Record<InstantUnavailableReason, MessageKey> = {
  needsCard: "cashoutRailPicker.addADebitCard",
  noFunds: "instantWhy.row.noFunds",
  partFunds: "instantWhy.row.partFunds",
  tooSmall: "instantWhy.row.tooSmall",
  dailyLimit: "instantWhy.row.dailyLimit",
  notNow: "instantWhy.row.notNow",
};

interface Props {
  /** Gross cashout total in MINOR units for the dominant currency. */
  amountCents: number;
  /** Lowercase ISO-4217 of that total. */
  currency: string;
  /** Selection lifts to the modal so Confirm can care about it later. */
  selectedRailId: string | null;
  onSelect: (railId: string) => void;
}

export default function CashoutRailPicker({ amountCents, currency, selectedRailId, onSelect }: Props) {
  const [data, setData] = useState<CashoutOptionsResponse | null>(null);
  const [failed, setFailed] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [cardBusy, setCardBusy] = useState(false);
  const [cardSheetCurrency, setCardSheetCurrency] = useState<string | null>(null); // open when set
  const [why, setWhy] = useState<{ reason: InstantUnavailableReason; rail: RailQuote } | null>(null);

  // The current selection, readable inside the fetch callback without making
  // it a dependency (a re-quote must not clobber a deliberate "Instant" tap).
  const selectedRef = useRef<string | null>(selectedRailId);
  selectedRef.current = selectedRailId;

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    paymentsApi
      .cashoutOptions(amountCents, currency)
      .then((data) => {
        if (cancelled) return;
        setData(data);
        // Preselect the server's default (cheapest) unless the talent already
        // picked a rail that is still on offer.
        const current = selectedRef.current;
        const stillOffered = current && data.rails.some((r) => r.railId === current && r.available);
        if (data.defaultRailId && !stillOffered) onSelect(data.defaultRailId);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
    // Re-quote when the total changes (e.g. earnings refresh under the modal)
    // or the app comes back to the foreground (refreshKey — see below).
    // onSelect is deliberately not a dependency — parent passes a setState.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amountCents, currency, refreshKey]);

  // Coming back from Stripe (the talent just added a debit card) → re-quote,
  // so "Instant eligible · Add card" becomes the real instant option.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") setRefreshKey((k) => k + 1);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  // Fallback only (no Stripe.js): the Stripe dashboard — same link as the
  // bank chip on the Balance page.
  const addCardViaStripe = useCallback(async () => {
    if (cardBusy) return;
    setCardBusy(true);
    try {
      const res = await stripeApi.dashboardLink();
      const url = res?.url;
      if (url) openExternal(url);
      else alert(t("cashoutRailPicker.couldnTOpenStripe"));
    } catch (err: any) {
      alert(err?.response?.data?.msg || t("cashoutRailPicker.couldnTOpenStripe2"));
    } finally {
      setCardBusy(false);
    }
  }, [cardBusy]);

  const addCard = (rail: RailQuote) => {
    if (canAddCardInApp) setCardSheetCurrency(rail.currency || currency);
    else addCardViaStripe();
  };

  // Endpoint unreachable (old server during a deploy) or still loading → show
  // nothing. The modal keeps its totals + Confirm; behavior is byte-identical
  // to before this component existed. Never block a cashout on a quote UI.
  if (failed || !data || data.rails.length === 0) return null;

  const money = (cents: number) => formatSingleAmount(cents / 100, currency);
  const multi = data.rails.length > 1;

  return (
    <div className="railpicker">
      {cardSheetCurrency && (
        <AddDebitCardSheet
          currency={cardSheetCurrency}
          onClose={() => setCardSheetCurrency(null)}
          onAdded={() => {
            // Card is on the account: re-quote (the server now offers the
            // real instant rail) and make Instant the selection.
            setCardSheetCurrency(null);
            setRefreshKey((k) => k + 1);
            onSelect(RAIL_STRIPE_INSTANT);
          }}
        />
      )}
      {why &&
        (instantExplainerFor(why.reason) === "needsCard" ? (
          // No debit card: the card-art popup Balance opens — one screen for "no card".
          <InstantPayoutsPopup
            state="needsCard"
            destination=""
            minimum=""
            onClose={() => setWhy(null)}
            onAddCard={() => {
              setWhy(null);
              addCard(why.rail);
            }}
          />
        ) : (
          <InstantUnavailablePopup variant={instantExplainerFor(why.reason) as "amount" | "unavailable"} onClose={() => setWhy(null)} />
        ))}
      <p className="railpicker-heading">{multi ? t("cashoutRailPicker.sendTo") : t("cashoutRailPicker.sendingTo")}</p>
      {data.rails.map((rail) => {
        // Instant, one debit card away: an invitation, not a disabled option.
        if (rail.needsCard) {
          return (
            <div key={rail.railId} className="railpicker-card railpicker-card--needs-card">
              <span className="railpicker-icon" aria-hidden>
                {railIcon(rail.railId)}
              </span>
              <span className="railpicker-main">
                <span className="railpicker-label">
                  {rail.label} · {rail.etaText}
                </span>
                <span className="railpicker-eta">{t("cashoutRailPicker.addADebitCard")}</span>
              </span>
              <button type="button" className="railpicker-cta" onClick={() => addCard(rail)} disabled={cardBusy}>
                {cardBusy ? t("cashoutRailPicker.opening") : t("cashoutRailPicker.addCard")}
              </button>
            </div>
          );
        }

        const selected = rail.railId === selectedRailId;
        const isInstant = rail.railId === RAIL_STRIPE_INSTANT;
        const walletList = rail.walletNames?.length ? rail.walletNames.slice(0, 3).join(" · ") : null;
        const cardLabel =
          isInstant && rail.destination?.last4
            ? `${rail.destination.brand || "Debit card"} ••${rail.destination.last4}`
            : null;
        // Grey Instant: not pickable, but a tap explains why (founder, 1.5.2) —
        // so it stays a live button (no aria-disabled: the tap is real).
        const whyReason = isInstant ? instantUnavailableReason(rail) : null;
        // MassPay wallet: MassPay's own fees to take it out of the wallet,
        // under the card (founder 10/29). No answer from MassPay → no table.
        const fees = rail.available && rail.withdrawFees?.options?.length ? rail.withdrawFees : null;
        return (
          <React.Fragment key={rail.railId}>
          <button
            key={rail.railId}
            type="button"
            disabled={!rail.available && !whyReason}
            className={`railpicker-card${selected ? " selected" : ""}${rail.available ? "" : " unavailable"}`}
            onClick={() => {
              if (rail.available) onSelect(rail.railId);
              else if (whyReason) setWhy({ reason: whyReason, rail });
            }}
            aria-pressed={selected}
          >
            <span className="railpicker-icon" aria-hidden>
              {railIcon(rail.railId)}
            </span>
            <span className="railpicker-main">
              <span className="railpicker-label">
                {isInstant ? `${rail.label} · ${rail.etaText}` : rail.label}
                {isInstant && rail.available && (
                  <span className="railpicker-check" aria-label={t("cashoutRailPicker.instantPayoutsOn")}>
                    ✓
                  </span>
                )}
                {walletList && <span className="railpicker-wallets">{walletList}</span>}
              </span>
              {rail.available ? (
                <span className="railpicker-eta">{isInstant ? cardLabel || t("cashoutRailPicker.debitCard") : rail.etaText}</span>
              ) : (
                <span className="railpicker-eta">
                  {whyReason && isKnownInstantLine(rail.unavailableReason) ? t(ROW_LINE[whyReason]) : rail.unavailableReason}
                </span>
              )}
            </span>
            {whyReason && (
              <span className="railpicker-why" aria-hidden>
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 11v5M12 8h.01" />
                </svg>
              </span>
            )}
            {rail.available && rail.netCents != null && (
              <span className="railpicker-amounts">
                <span className="railpicker-net">
                  {rail.isEstimate ? "≈ " : ""}
                  {money(rail.netCents)}
                </span>
                <span className="railpicker-fee">
                  {rail.feeCents === 0
                    ? t("cashoutRailPicker.free")
                    : rail.feePct != null
                      ? t("cashoutRailPicker.fee", { feePct: rail.feePct })
                      : t("cashoutRailPicker.fee2", { amount: money(rail.feeCents!) })}
                </span>
              </span>
            )}
          </button>
          {fees && (
            <div className="railpicker-mpfees">
              <p className="railpicker-mpfees-k">{t("fanCashout.thenWithdrawTo")}</p>
              <MasspayFeeTable fees={fees} />
              <SmallCashoutLine amount={fees.amount} />
            </div>
          )}
          </React.Fragment>
        );
      })}
    </div>
  );
}
